import React, { useState, useRef, useEffect } from 'react';
import { FiSearch, FiShoppingBag, FiUser, FiMenu, FiX, FiCheck, FiMail, FiFilter, FiX as FiClose, FiStar, FiChevronDown } from 'react-icons/fi';
import ReactDOM from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import CartButton from './CartButton';

// Size category interface
interface SizeCategory {
  name: string;
  sizes: string[];
}

// Separate filter modal component
const FilterModal = ({ 
  isOpen, 
  onClose, 
  minPrice, 
  setMinPrice, 
  maxPrice, 
  setMaxPrice,
  selectedSizes,
  toggleSize,
  selectedBrands,
  toggleBrand,
  resetFilters,
  applyFilters,
  sizeCategories,
  availableBrands
}: {
  isOpen: boolean;
  onClose: () => void;
  minPrice: number;
  setMinPrice: (price: number) => void;
  maxPrice: number;
  setMaxPrice: (price: number) => void;
  selectedSizes: string[];
  toggleSize: (size: string) => void;
  selectedBrands: string[];
  toggleBrand: (brand: string) => void;
  resetFilters: () => void;
  applyFilters: () => void;
  sizeCategories: SizeCategory[];
  availableBrands: string[];
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Portal to render outside of parent component hierarchy
  return ReactDOM.createPortal(
    <div 
      className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center p-4" 
      style={{ zIndex: 9999 }}
    >
      <div 
        ref={modalRef}
        className="bg-[#0a0a0a] rounded-lg shadow-xl w-full max-w-md md:max-w-lg overflow-hidden"
        style={{ maxHeight: 'calc(100vh - 40px)' }}
      >
        <div className="sticky top-0 z-10 bg-[#0a0a0a] p-4 border-b border-gray-800 flex justify-between items-center">
          <h3 className="text-lg font-medium text-white">Filter Products</h3>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-gray-800 rounded-full transition-colors text-white"
          >
            <FiClose className="h-5 w-5" />
          </button>
        </div>
        
        <div className="p-4 space-y-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 160px)' }}>
          {/* Price Range */}
          <div>
            <h4 className="text-sm font-medium mb-3 text-white">Price Range</h4>
            <div className="flex space-x-4">
              <div className="flex-1">
                <label className="block text-xs text-gray-400 mb-1">Min</label>
                <input
                  type="number"
                  min="0"
                  max={maxPrice}
                  value={minPrice}
                  onChange={(e) => setMinPrice(Number(e.target.value))}
                  className="w-full bg-[#1a1a1a] border border-gray-700 rounded-md px-3 py-2 text-sm text-white"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-400 mb-1">Max</label>
                <input
                  type="number"
                  min={minPrice}
                  max="9000"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(Number(e.target.value))}
                  className="w-full bg-[#1a1a1a] border border-gray-700 rounded-md px-3 py-2 text-sm text-white"
                />
              </div>
            </div>
          </div>
          
          {/* Sizes */}
          <div>
            <h4 className="text-sm font-medium mb-3 text-white">Sizes</h4>
            
            {sizeCategories.map(category => (
              <div key={category.name} className="mb-4">
                <h5 className="text-xs font-medium text-accent mb-2">{category.name}</h5>
                <div className="grid grid-cols-5 gap-2">
                  {category.sizes.map(size => (
                    <button
                      key={size}
                      onClick={() => toggleSize(size)}
                      className={`py-1 px-1 rounded-md text-xs font-medium transition-colors ${
                        selectedSizes.includes(size) 
                          ? 'bg-accent text-white' 
                          : 'bg-[#1a1a1a] text-white hover:bg-gray-800'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          {/* Brands */}
          <div>
            <h4 className="text-sm font-medium mb-3 text-white">Brands</h4>
            <div className="space-y-2">
              {availableBrands.map(brand => (
                <div key={brand} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`brand-${brand}`}
                    checked={selectedBrands.includes(brand)}
                    onChange={() => toggleBrand(brand)}
                    className="h-4 w-4 rounded border-gray-700 text-accent focus:ring-accent"
                  />
                  <label htmlFor={`brand-${brand}`} className="ml-2 text-sm text-white">
                    {brand}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="sticky bottom-0 z-10 bg-[#0a0a0a] p-4 border-t border-gray-800 flex justify-between">
          <button 
            onClick={resetFilters}
            className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white transition-colors"
          >
            Reset All
          </button>
          <button 
            onClick={applyFilters}
            className="px-4 py-2 bg-accent text-white text-sm font-medium rounded-md hover:bg-accent/90 transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(9000);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [sizeDropdownOpen, setSizeDropdownOpen] = useState(false);
  const sizeDropdownRef = useRef<HTMLDivElement>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Organized shoe sizes by category
  const sizeCategories: SizeCategory[] = [
    {
      name: "Men",
      sizes: ['7M', '7.5M', '8M', '8.5M', '9M', '9.5M', '10M', '10.5M', '11M', '11.5M', '12M', '12.5M', '13M', '14M', '15M', '16M', '17M', '18M']
    },
    {
      name: "Women",
      sizes: ['5W', '5.5W', '6W', '6.5W', '7W', '7.5W', '8W', '8.5W', '9W', '9.5W', '10W', '10.5W', '11W', '11.5W', '12W']
    },
    {
      name: "Grade School (GS)",
      sizes: ['3.5Y', '4Y', '4.5Y', '5Y', '5.5Y', '6Y', '6.5Y', '7Y']
    },
    {
      name: "Toddler (TD)",
      sizes: ['2C', '3C', '4C', '5C', '6C', '7C', '8C', '9C', '10C']
    }
  ];

  // Size range groups for the dropdown
  const sizeRangeGroups = [
    { name: "3.5Y - 7Y (GS)", range: ["3.5Y", "4Y", "4.5Y", "5Y", "5.5Y", "6Y", "6.5Y", "7Y"] },
    { name: "8M", range: ["8M"] },
    { name: "8.5M", range: ["8.5M"] },
    { name: "9M", range: ["9M"] },
    { name: "9.5M", range: ["9.5M"] },
    { name: "10M", range: ["10M"] },
    { name: "10.5M", range: ["10.5M"] },
    { name: "11M", range: ["11M"] },
    { name: "11.5M", range: ["11.5M"] },
    { name: "12M", range: ["12M"] },
    { name: "12.5M", range: ["12.5M"] },
    { name: "13-15M", range: ["13M", "14M", "15M"] }
  ];

  // Available brands
  const availableBrands = ['Nike', 'Adidas', 'Jordan', 'Yeezy', 'Supreme', 'Bape', 'Off-White', 'Gucci', 'Louis Vuitton'];

  const toggleSize = (size: string) => {
    if (selectedSizes.includes(size)) {
      setSelectedSizes(selectedSizes.filter(s => s !== size));
    } else {
      setSelectedSizes([...selectedSizes, size]);
    }
  };

  const toggleBrand = (brand: string) => {
    if (selectedBrands.includes(brand)) {
      setSelectedBrands(selectedBrands.filter(b => b !== brand));
    } else {
      setSelectedBrands([...selectedBrands, brand]);
    }
  };

  const applyFilters = () => {
    // In a real app, you would apply these filters to your product data
    console.log('Applied filters:', {
      price: { min: minPrice, max: maxPrice },
      sizes: selectedSizes,
      brands: selectedBrands
    });
    setFilterModalOpen(false);
  };

  const resetFilters = () => {
    setMinPrice(0);
    setMaxPrice(9000);
    setSelectedSizes([]);
    setSelectedBrands([]);
  };

  // Function to navigate to inventory with size filter
  const navigateToSizeFilter = (range: string[]) => {
    // Different behavior based on the range
    if (range.length === 1) {
      // For single size, pass directly to show exact size matches
      let sizeParam = range[0];
      
      // Ensure proper format with suffix
      if (!sizeParam.match(/[a-zA-Z]/) && !isNaN(parseFloat(sizeParam))) {
        sizeParam = `${sizeParam}M`; // Add 'M' for Men's sizes if missing
      }
      
      // Log to console for debugging
      console.log(`Header: Navigating to size filter with parameter: ${sizeParam}`);
      
      // Always route to inventory page
      const baseUrl = window.location.origin;
      const inventoryPath = `/inventory?sizes=${sizeParam}`;
      window.location.href = baseUrl + inventoryPath;
    } else {
      // For range of sizes, pass all to filter by any in the range
      // Ensure all numeric sizes have the appropriate suffix
      const normalizedSizes = range.map(size => {
        if (!size.match(/[a-zA-Z]/) && !isNaN(parseFloat(size))) {
          return `${size}M`; // Add 'M' for Men's sizes if missing
        }
        return size;
      });
      
      const sizeParams = normalizedSizes.join(',');
      
      // Log to console for debugging
      console.log(`Header: Navigating to size filter with parameters: ${sizeParams}`);
      
      // Always route to inventory page
      const baseUrl = window.location.origin;
      const inventoryPath = `/inventory?sizes=${sizeParams}`;
      window.location.href = baseUrl + inventoryPath;
    }
    setSizeDropdownOpen(false);
  };

  // Handle click outside of size dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sizeDropdownRef.current && !sizeDropdownRef.current.contains(event.target as Node)) {
        setSizeDropdownOpen(false);
      }
    };
    
    if (sizeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sizeDropdownOpen]);

  // Handle search submit
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to inventory page with search query
      navigate(`/inventory?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
    }
  };

  // Focus search input when search is opened
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  // Close search on escape key
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [searchOpen]);

  return (
    <header className="sticky top-0 z-20 bg-background-dark/95 backdrop-blur-sm border-b border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Utility Links */}
        <div className="py-2.5 sm:py-3 grid grid-cols-3 border-b border-gray-800">
          <div className="flex items-center justify-start">
            {/* Left side logo */}
            <a href="/" className="flex items-center">
              <img 
                src="/images/grailed804logo.webp" 
                alt="GRAILED804 Logo" 
                className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover border border-gray-700"
              />
            </a>
          </div>
          
          <div className="flex items-center justify-center">
            <FiCheck className="h-4 w-4 text-accent mr-2" />
            <a href="/#authenticity" className="text-sm font-medium text-primary hover:text-accent transition-colors">
              <span className="text-accent">100% Authenticity</span> Guarantee
            </a>
          </div>
          
          <div className="flex justify-end">
            <a href="/#contact" className="text-sm font-medium text-primary hover:text-accent transition-colors flex items-center">
              <FiMail className="h-4 w-4 mr-2" />
              Contact Us
            </a>
          </div>
        </div>
        
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <a href="/" className="text-2xl font-bold tracking-tighter hover:text-accent transition-colors">
              GRAILED804
            </a>
          </div>

          {/* Desktop Navigation - centered */}
          <nav className="hidden md:flex items-center space-x-5">
            <a href="/#featured" className="text-primary hover:text-accent transition-colors">
              Shop Heat
            </a>
            <a href="/#categories" className="text-primary hover:text-accent transition-colors">
              Shop By Category
            </a>
            <div className="relative" ref={sizeDropdownRef}>
              <button 
                onClick={() => setSizeDropdownOpen(!sizeDropdownOpen)}
                className="text-primary hover:text-accent transition-colors flex items-center"
              >
                Shop By Size
                <FiChevronDown className="ml-1 h-4 w-4" />
              </button>
              {sizeDropdownOpen && (
                <div className="absolute left-0 mt-2 w-44 bg-background-dark border border-gray-800 rounded-md shadow-lg z-30">
                  {sizeRangeGroups.map((group, index) => (
                    <button
                      key={index}
                      onClick={() => navigateToSizeFilter(group.range)}
                      className="block w-full text-left px-4 py-2 text-sm text-primary hover:bg-background-light hover:text-accent transition-colors"
                    >
                      {group.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <a href="/inventory" className="text-primary hover:text-accent transition-colors">
              Shop All Inventory
            </a>
            <a href="/#authenticity" className="text-primary hover:text-accent transition-colors">Authenticity</a>
            <a href="/#reviews" className="text-primary hover:text-accent transition-colors">Our Reviews</a>
          </nav>

          {/* Desktop Actions */}
          <div className="flex items-center space-x-4">
            {/* Search button and expanded search input */}
            <div className="relative">
              <button 
                onClick={() => setSearchOpen(!searchOpen)}
                className="p-2 text-primary hover:text-accent transition-colors"
              >
                <FiSearch className="h-5 w-5" />
              </button>
              
              {/* Expanded search input */}
              {searchOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-background-dark border border-gray-800 rounded-md shadow-lg z-30 p-2">
                  <form onSubmit={handleSearchSubmit} className="flex items-center">
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search products..."
                      className="flex-1 p-2 bg-background-light text-primary border border-gray-700 rounded-md focus:ring-accent focus:border-accent"
                    />
                    <button 
                      type="submit"
                      className="ml-2 p-2 bg-accent text-white rounded-md hover:bg-accent/90"
                    >
                      <FiSearch className="h-4 w-4" />
                    </button>
                  </form>
                </div>
              )}
            </div>
            
            <CartButton />
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-primary hover:text-accent transition-colors"
            >
              {mobileMenuOpen ? <FiX className="h-6 w-6" /> : <FiMenu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-light border-t border-gray-800">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <a href="/" className="block px-3 py-2 text-xl font-bold tracking-tighter text-primary hover:bg-background">GRAILED804</a>
            <a 
              href="/#featured" 
              className="block px-3 py-2 text-primary hover:bg-background text-base"
              onClick={() => setMobileMenuOpen(false)}
            >
              Shop Heat
            </a>
            <a 
              href="/#categories" 
              className="block px-3 py-2 text-primary hover:bg-background text-base"
              onClick={() => setMobileMenuOpen(false)}
            >
              Shop By Category
            </a>
            
            {/* Mobile Shop By Size */}
            <div className="px-3 py-2">
              <div className="text-primary text-base mb-2">Shop By Size</div>
              <div className="grid grid-cols-2 gap-2">
                {sizeRangeGroups.map((group, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      navigateToSizeFilter(group.range);
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left py-1.5 px-3 text-sm text-primary hover:text-accent bg-background-dark rounded-md hover:bg-background-dark/80"
                  >
                    {group.name}
                  </button>
                ))}
              </div>
            </div>
            
            <a 
              href="/inventory" 
              className="block px-3 py-2 text-primary hover:bg-background text-base"
              onClick={() => setMobileMenuOpen(false)}
            >
              Shop All Inventory
            </a>
            <a 
              href="/#authenticity" 
              className="block px-3 py-2 text-primary hover:bg-background text-base"
              onClick={() => setMobileMenuOpen(false)}
            >
              Authenticity
            </a>
            <a 
              href="/#reviews" 
              className="block px-3 py-2 text-primary hover:bg-background text-base"
              onClick={() => setMobileMenuOpen(false)}
            >
              Our Reviews
            </a>
          </div>
          <div className="pt-4 pb-3 border-t border-gray-800">
            <div className="flex items-center justify-around px-5">
              {/* Mobile search in menu */}
              <form onSubmit={handleSearchSubmit} className="flex items-center w-full px-3">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products..."
                  className="flex-1 p-2 bg-background-dark text-primary border border-gray-700 rounded-md focus:ring-accent focus:border-accent"
                />
                <button 
                  type="submit"
                  className="ml-2 p-2 bg-accent text-white rounded-md"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <FiSearch className="h-4 w-4" />
                </button>
              </form>
              
              <CartButton />
            </div>
          </div>
        </div>
      )}
      
      {/* Render the FilterModal as a portal */}
      <FilterModal
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        minPrice={minPrice}
        setMinPrice={setMinPrice}
        maxPrice={maxPrice}
        setMaxPrice={setMaxPrice}
        selectedSizes={selectedSizes}
        toggleSize={toggleSize}
        selectedBrands={selectedBrands}
        toggleBrand={toggleBrand}
        resetFilters={resetFilters}
        applyFilters={applyFilters}
        sizeCategories={sizeCategories}
        availableBrands={availableBrands}
      />
    </header>
  );
};

export default Header; 